// GroupFaces demo film (landing-page hero): the app recreated in code, filmed on tilted planes with a shallow depth
// of field. Nine shots, 52 s, music only, short lines and the brand at the end. `locale` picks the string table
// (i18n.ts): Demo-GroupFaces is pt, Demo-GroupFaces-EN is en.
import React from 'react';
import {AbsoluteFill, Audio, Img, interpolate, staticFile} from 'remotion';
import {Fonts} from './Fonts';
import {C, F, FPS, ease, easeOut} from './theme';
import {useT} from './data';
import {LocaleContext, STRINGS, type Locale} from './i18n';
import {Lens, Post, Shot} from './lens';
import {CLI, Clients, DEL, Delivery, GAL, Galleries, OVER, Overview, SEND, SIDEBAR, Send, Sidebar, TARGETS, Targets, delRowY, metricXY, targetXY} from './app';
import {ramp} from './motion/util';

export const DURATION = 52;
const BG = '#030304';
const H = {a: 180, c: 0.5, w: 0.1, f: 0.2};  // horizontal focus band through the frame centre

// A short line on black: rises out of a blur, holds, leaves.
export const Line: React.FC<{parts: [string, string][]; a: number; b: number}> = ({parts, a, b}) => {
 const t = useT();
 const k = Math.min(ramp(t, a, 0.9, easeOut), 1 - ramp(t, b - 0.5, 0.5, ease));
 if (k <= 0) return null;
 return <AbsoluteFill style={{alignItems: 'center', justifyContent: 'center'}}>
  <div style={{fontFamily: F.sans, fontSize: 68, fontWeight: 500, letterSpacing: '-.02em', whiteSpace: 'nowrap', opacity: k,
   filter: `blur(${((1 - k) * 12).toFixed(2)}px)`, transform: `translateY(${((1 - k) * 14).toFixed(2)}px) scale(${(1.0 + 0.012 * ramp(t, a, b - a)).toFixed(4)})`}}>
   {parts.map(([s, color], i) => <span key={i} style={{color}}>{s}</span>)}
  </div>
 </AbsoluteFill>;
};

// Closing mark: the GroupFaces logo settles and the address rises behind it.
export const Mark: React.FC<{a: number}> = ({a}) => {
 const t = useT();
 const k = ramp(t, a, 0.9, easeOut), k2 = ramp(t, a + 0.35, 0.7, easeOut);
 if (k <= 0) return null;
 return <AbsoluteFill style={{alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 34}}>
  <Img src={staticFile('brand/logo.svg')} style={{height: 88, width: 'auto', opacity: k, filter: `blur(${((1 - k) * 10).toFixed(2)}px)`, transform: `scale(${(0.94 + 0.06 * k).toFixed(4)})`}}/>
  <div style={{fontFamily: F.mono, fontSize: 22, letterSpacing: '.26em', color: C.ink2, opacity: k2, transform: `translateY(${((1 - k2) * 10).toFixed(2)}px)`}}>groupfaces.io</div>
 </AbsoluteFill>;
};

export type FilmProps = {locale: Locale};
export const Film: React.FC<FilmProps> = ({locale}) => {
 const s = STRINGS[locale];
 return <AbsoluteFill style={{background: BG}}>
 <LocaleContext.Provider value={locale}>
 <Fonts>
  {/* 1 · sidebar, low angle, "Projects" lights up */}
  <Shot from={0} to={5.5} inn={0.8}>
   <Lens W={SIDEBAR.W} H={SIDEBAR.H} cam={[{px: 250, py: 386, sx: 820, sy: 520, rx: 42, ry: -30, rz: 9, z: -260}, {px: 250, py: 386, sx: 820, sy: 520, rx: 32, ry: -17, rz: 5, z: 230}]}
    focus={[{...H, c: 0.48, w: 0.05, f: 0.17}, {...H, c: 0.48, w: 0.06, f: 0.17}]}><Sidebar lightAt={2.1}/></Lens>
  </Shot>
  {/* 2 · reference photos drop in, one face each; tilt down across the cards */}
  <Shot from={5.0} to={11.5}>
   <Lens W={TARGETS.W} H={TARGETS.H} cam={[{px: 900, py: 510, rx: 30, ry: -5, z: -330}, {px: 900, py: 1190, rx: 19, ry: 3, z: -260}]}
    focus={[{...H, w: 0.12}, {...H, w: 0.12}]}><Targets times={[0.5, 1.1, 2.3, 2.9, 4.1]}/></Lens>
  </Shot>
  {/* 3 · folders become galleries, the count climbs; slide right */}
  <Shot from={11.0} to={17.4}>
   <Lens W={GAL.W} H={GAL.H} cam={[{px: 450, py: 690, rx: 10, ry: 27, z: -400}, {px: 2060, py: 600, rx: 10, ry: 19, z: -380}]}
    focus={[{a: 90, c: 0.5, w: 0.16, f: 0.22}, {a: 90, c: 0.52, w: 0.17, f: 0.22}]}><Galleries times={[0.5, 1.5, 2.5, 3.5]}/></Lens>
  </Shot>
  {/* 4 · line on black */}
  <Shot from={17.0} to={22.3} inn={0.3} out={0.3}>
   <Line a={0.45} b={5.0} parts={[[s.line1[0], C.muted], [s.line1[1], C.ink]]}/>
  </Shot>
  {/* 5 · processing, then the metrics count up; push in on "Clientes identificados" */}
  <Shot from={21.9} to={30.5}>
   <Lens W={OVER.W} H={OVER.H} cam={[{px: 1000, py: 515, rx: 26, ry: -14, rz: 6, z: -560}, {px: metricXY(2).x + 300, py: metricXY(2).y + 150, rx: 17, ry: -22, rz: 4, z: 90}]}
    focus={[{...H, w: 0.15}, {...H, w: 0.09}]}><Overview doneAt={3.2}/></Lens>
  </Shot>
  {/* 6 · Clientes tab, one gallery per student; long lateral move, the focus band rolls across the rows */}
  <Shot from={30.0} to={37.5}>
   <Lens W={CLI.W} H={CLI.H} cam={[{px: 620, py: 560, rx: 26, ry: -25, z: -300}, {px: 2150, py: 1150, rx: 22, ry: -13, z: -260}]}
    focus={[{a: 172, c: 0.36, w: 0.09, f: 0.2}, {a: 172, c: 0.62, w: 0.09, f: 0.2}]}><Clients/></Lens>
  </Shot>
  {/* 7 · macro on "Enviar galerias", cursor press, the button pops */}
  <Shot from={37.0} to={41.6}>
   <Lens W={SEND.W} H={SEND.H} cam={[{px: SEND.bx + SEND.bw / 2, py: SEND.by + SEND.bh / 2, rx: 17, ry: -26, z: 300}, {px: SEND.bx + SEND.bw / 2, py: SEND.by + SEND.bh / 2, rx: 12, ry: -15, z: 430}]}
    focus={[{cx: 0.5, cy: 0.5, r: 0.11, f: 0.2}, {cx: 0.5, cy: 0.5, r: 0.13, f: 0.2}]} blur={11}><Send pressAt={2.3}/></Lens>
  </Shot>
  {/* 8 · per-student link status: enviado → aberto → baixado, with date and time; close on the chips */}
  <Shot from={41.1} to={47.4}>
   <Lens W={DEL.W} H={DEL.H} cam={[{px: 760, py: delRowY(1) + 85, rx: 28, ry: 22, z: -230}, {px: 1180, py: delRowY(2) + 85, rx: 20, ry: 12, z: 60}]}
    focus={[{...H, w: 0.13}, {...H, w: 0.1}]}><Delivery/></Lens>
  </Shot>
  {/* 9 · closing line, then the brand mark */}
  <Shot from={47.0} to={DURATION} out={0}>
   <Line a={0.4} b={2.9} parts={[[s.line2[0], C.ink], [s.line2[1], C.accentHi]]}/>
   <Mark a={2.9}/>
  </Shot>
 </Fonts>
 </LocaleContext.Provider>
 <Post/>
 <Music/>
 </AbsoluteFill>;
};

const MUSIC = 'audio/groupfaces-demo-music.wav';
const Music: React.FC = () => <Audio src={staticFile(MUSIC)} volume={f => interpolate(f, [0, FPS * 0.8, FPS * (DURATION - 2.5), FPS * DURATION - 1], [0, 1, 1, 0], {extrapolateLeft: 'clamp', extrapolateRight: 'clamp'})}/>;

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
